import React, {useContext, useEffect, useMemo} from "react";
import GridSection from "../../../components/grid/GridSection.jsx";
import {useBrand} from "../../../utils/brands/branding.js";
import {FilterContext} from "../../../components/filters/DashboardFilterContextProvider.jsx";
import {useMarketingColumns} from "./channelGridColumnSetup.js";
import {ALWAYS_LAST_KEY} from "../../../utils/dataGridSorting.js";
import {useTranslation} from "react-i18next";
import updateTotal, {calculateMarketingChannelResults} from "../../../utils/datagridTotal.js";
import useUserSettings from "../../../utils/useUserSettings.js";
import DataGrid2 from "../../../components/datagrid/DataGrid2.jsx";

export function ChannelGridSection({
                                       channel,
                                       channelData,
                                       gridType,
                                       setChannelTotal,
                                       setModalCampaignRow
                                   }) {
    const {
        sourceRegister,
        campaignRegister,
        campaignsAllowListed,
        campaignSearchFilter,
        sourcesRemoved,
    } = useContext(FilterContext);

    const {t} = useTranslation();
    const brand = useBrand(channel.name);
    const columns = useMarketingColumns(gridType);
    const [userSettings] = useUserSettings();

    // Build dataSources WITHOUT side-effects
    const dataSources = useMemo(() => {
        if (channelData == null) return [];

        return Object.values(channelData.adAccounts ?? {}).flatMap((adAccount) => {
            const rows = [];
            let totalRow = {name: t("project.total"), [ALWAYS_LAST_KEY]: true};
            let foundAny = false;

            Object.values(adAccount.campaigns).forEach((campaign) => {
                let filteredOut = false;

                if (
                    campaignSearchFilter.length > 0 &&
                    !campaign.name.toLowerCase().includes(campaignSearchFilter.toLowerCase())
                ) {
                    filteredOut = true;
                }
                if (
                    campaignsAllowListed.length > 0 &&
                    !campaignsAllowListed.includes(campaign.remoteId)
                ) {
                    filteredOut = true;
                }

                if (!filteredOut) {
                    foundAny = true;
                    rows.push(campaign);
                    columns.forEach((col) => {
                        totalRow = updateTotal(totalRow, col, campaign[col.name]);
                    });
                }
            });

            if (!sourcesRemoved.includes(channel.name) && foundAny) {
                rows.push(totalRow);
                return [
                    {
                        name: adAccount.name,
                        remoteId: adAccount.remoteId,
                        rows
                    }
                ];
            }
            return [];
        });
    }, [
        channelData,
        sourcesRemoved,
        campaignsAllowListed,
        campaignSearchFilter,
        userSettings,
        columns,
        t
    ]);

    // After render, register campaigns & sources and pass totals upward
    useEffect(() => {
        // 1) Register each campaign and its integrationName
        dataSources.forEach((ds) => {
            ds.rows.forEach((row) => {
                if (!row[ALWAYS_LAST_KEY]) {
                    campaignRegister(row);
                    sourceRegister(row.integrationName);
                }
            });
        });

        // 2) Compute totals map and update parent
        const totalsMap = {};
        dataSources.forEach((ds) => {
            const last = ds.rows[ds.rows.length - 1];
            if (last && last[ALWAYS_LAST_KEY]) {
                totalsMap[`${channel.id}:${ds.remoteId}`] = last;
            }
        });
        setChannelTotal((prev) => ({
            ...Object.fromEntries(
                Object.entries(prev).filter(([key]) => !key.startsWith(`${channel.id}:`))
            ),
            ...totalsMap,
        }));
    }, [channel.id, dataSources, campaignRegister, sourceRegister, setChannelTotal]);

    // No data
    if (dataSources.length === 0) {
        return null;
    }

    // Render grids
    const calculateTitle = (channel, ds) => {
        if (channel.name === ds.name) return brand.name;
        if (ds.name.startsWith(brand.name)) return ds.name;
        return brand.name + " | " + ds.name;
    };


    return dataSources.map((ds) => (
        <GridSection
            brand={brand}
            title={calculateTitle(channel, ds)}
            key={`${channel.id}-${ds.remoteId}`}
            gridType={gridType}
        >
            <DataGrid2
                group="marketing-profitability"
                dataSource={ds.rows}
                columns={columns}
                showHeader
                hasTotal
                onRowDoubleClick={setModalCampaignRow}
            />
        </GridSection>
    ));
}
